import React from "react";
import {
  InsuredDataValues,
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../interfaces";
import { fileNameFormat, reduceObj } from "../../../common/services/Utilities";
import SearchPills from "../../../containers/SearchPills";
import { Box, Divider, Typography, styled } from "@mui/material";
import MultiHeaderSummaryTable from "../../MultiHeaderTable/MultiHeaderSummaryTable";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import {
  primaryHeaderColor,
  textHeaderColor,
} from "../../../refdata/constants";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import {
  addDataIfNull,
  transformData,
  transformDataForDownload,
} from "./SummaryTableUtilities";
import ShowHideButton from "../../ShowHideButton";

const Root = styled("div")(({ theme }) => ({
  "& #multi-header-table-head": {
    "position": "sticky",
    "top": 0,
    "& tr": {
      position: "static",
      borderCollaps: "collapse",
    },
    "& th": {
      position: "static",
      borderCollaps: "collapse",
    },
  },
}));

interface Props {
  appState: Query;
}

export default function SummaryTablesByStateCharter({ appState }: Props) {
  const [totalResults, setTotalResults] = React.useState<KeyValue>({});
  const [firstColumnData, setFirstColumnData] = React.useState<KeyValue>({});
  const [downloadData, setDownloadData] = React.useState<KeyValue[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [previousYear, setPreviousYear] = React.useState(
    appState.filters.lastYear,
  );
  const [previousState, setPreviousState] = React.useState(
    appState.filters.stalpbr ? appState.filters.stalpbr : "",
  );
  const [previousCounty, setPreviousCounty] = React.useState(
    appState.filters.county ? appState.filters.county : "",
  );
  const [tableDataList, setTableDataList] = React.useState<{
    [key: string]: InsuredDataValues;
  }>({});

  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
    {
      label: "State",
      value: `${appState.filters.stalpbr}`,
      search: false,
      static: true,
      formKey: "stalpbr",
    },
    {
      label: "County",
      value: `${appState.filters.county}`,
      search: false,
      static: false,
      formKey: "county",
    },
  ];
  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(
        "Summary_Tables_By_State_County_formatted",
      )}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      footnotes: [
        appState.filters.lastYear === "1994"
          ? "*Includes 18 conservatorships"
          : null,
      ].filter(Boolean) as string[],
    },
  ];

  const headingTitle = (
    <Box
      sx={{
        marginTop: -1,
        display: "flex",
        alignItems: "end",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        Individual State Totals by County
      </Typography>
      <ApiDataDownload
        app="SOD"
        apiData={apiData}
        downloadData={downloadData}
      />
    </Box>
  );

  const countyHeader: TableHeader[] = [
    {
      title: "County",
      styles: {
        backgroundColor: primaryHeaderColor,
        color: textHeaderColor,
        textAlign: "left",
      },
      colSpan: 3,
      apiField: "",
    },
  ];

  React.useEffect(() => {
    if (
      initialLoad ||
      appState.filters.stalpbr !== previousState ||
      appState.filters.county !== previousCounty ||
      appState.filters.lastYear !== previousYear
    ) {
      setLoading(true);
      initialLoad && setInitialLoad(false);
      setPreviousState(appState.filters.stalpbr);
      setPreviousCounty(appState.filters.county);
      setPreviousYear(appState.filters.lastYear);
      let isMounted = true;
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?filters=STALPBR:"${
              appState.filters.stalpbr
            }" AND YEAR:${appState.filters.lastYear}
            &agg_by=CNTYNAMB&get_unique=CERT&agg_sum_fields=DEPSUMBR&agg_limit=10000`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=STALPBR:"${
              appState.filters.stalpbr
            }" AND YEAR:${appState.filters.lastYear} AND INSURED:CB
            &agg_by=CNTYNAMB&get_unique=CERT&agg_sum_fields=DEPSUMBR&agg_limit=10000`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=STALPBR:"${
              appState.filters.stalpbr
            }" AND YEAR:${appState.filters.lastYear} AND INSURED:SA
            &agg_by=CNTYNAMB&get_unique=CERT&agg_sum_fields=DEPSUMBR&agg_limit=10000`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=STALPBR:"${
              appState.filters.stalpbr
            }" AND YEAR:${
              appState.filters.lastYear
            } AND INSURED:IB&agg_limit=10000
            &agg_by=CNTYNAMB&get_unique=CERT&agg_sum_fields=DEPSUMBR`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=INSURED&agg_sum_fields=DEPSUMBR&get_unique=CERT&filters=STALPBR:"${
              appState.filters.stalpbr
            }" 
            AND YEAR:${appState.filters.lastYear}`,
          ),
        ])
          .then((response) => {
            if (isMounted) {
              let allCounties: string[] = response[0].data.map(
                (e: KeyValue) => e.CNTYNAMB,
              );
              if (appState.filters.county) {
                allCounties = [appState.filters.county];
              }
              const countyDataMap: KeyValue = {};
              let key = 0;
              for (const value of allCounties) {
                countyDataMap[key] = value;
                key = key + 1;
              }
              setFirstColumnData(countyDataMap);
              let tableData: KeyValue = reduceObj(
                allCounties.map((county: string) => {
                  return {
                    key: county,
                    value: {
                      all: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
                      CB: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
                      SA: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
                      IB: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
                    },
                  };
                }),
              );
              for (const data of response[0].data) {
                const county: string = data.CNTYNAMB;
                if (allCounties.includes(county)) {
                  tableData[county]["all"] = {
                    unique_count: data.unique_count,
                    sum_DEPSUMBR: data.sum_DEPSUMBR,
                    count: data.count,
                  };
                }
              }
              for (const data of response[1].data) {
                const county: string = data.CNTYNAMB;
                if (allCounties.includes(county)) {
                  tableData[county]["CB"] = {
                    unique_count: data.unique_count,
                    sum_DEPSUMBR: data.sum_DEPSUMBR,
                    count: data.count,
                  };
                }
              }
              for (const data of response[2].data) {
                const county: string = data.CNTYNAMB;
                if (allCounties.includes(county)) {
                  tableData[county]["SA"] = {
                    unique_count: data.unique_count,
                    sum_DEPSUMBR: data.sum_DEPSUMBR,
                    count: data.count,
                  };
                }
              }
              for (const data of response[3].data) {
                const county: string = data.CNTYNAMB;
                if (allCounties.includes(county)) {
                  tableData[county]["IB"] = {
                    unique_count: data.unique_count,
                    sum_DEPSUMBR: data.sum_DEPSUMBR,
                    count: data.count,
                  };
                }
              }
              let totalResults: KeyValue = {};
              const totalsInusredTypes = transformData(
                addDataIfNull(response[4].data),
              );
              const totalsAll: KeyValue = {
                all: {
                  count: 0,
                  unique_count: 0,
                  sum_DEPSUMBR: 0,
                },
              };
              for (const data of response[4].data) {
                totalsAll["all"].count = totalsAll["all"].count + data.count;
                totalsAll["all"].unique_count =
                  totalsAll["all"].unique_count + data.unique_count;
                totalsAll["all"].sum_DEPSUMBR =
                  totalsAll["all"].sum_DEPSUMBR + data.sum_DEPSUMBR;
              }
              totalResults = { ...totalsAll, ...totalsInusredTypes };
              let totalsRow: KeyValue = {
                "Totals / Unique Institutions": totalResults,
              };
              // This essentially changes the 'totals' row to just one county's total if one is selected.
              if (allCounties.length === 1 && appState.filters.county) {
                totalResults = tableData[appState.filters.county];
              }
              // This removes the totals row from the xls if there is only one county
              if (allCounties.length === 1) {
                totalsRow = {};
              }
              const tableDataWithTotals = { ...tableData, ...totalsRow };
              const resultForDownload = transformDataForDownload(
                tableDataWithTotals,
                `06/30/${appState.filters.lastYear}`,
                "County",
                `${appState.filters.stalpbr}`,
              );
              setDownloadData(resultForDownload);
              setTotalResults(totalResults);
              setTableDataList(tableData);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [
    appState.filters,
    initialLoad,
    previousState,
    previousCounty,
    previousYear,
  ]);

  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      <Root>
        <MultiHeaderSummaryTable
          tableData={tableDataList}
          totalsData={totalResults}
          firstColumnData={firstColumnData}
          firstHeader={countyHeader}
          showAsterisks={false}
          loading={loading}
          tableType={"county"}
          lastYear={appState.filters.lastYear}
        />
      </Root>
      {appState.filters.lastYear === "1994" && (
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 700,
            minWidth: 700,
            fontSize: 16,
            whiteSpace: "nowrap",
          }}
        >
          *Includes 18 conservatorships
        </Typography>
      )}
    </>
  );
}
